import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  GraduationCap, BookOpen, Sparkle, Cloud, Hourglass as HGIcon, Scales, Cloud as CloudIcon,
  ArrowRight, Copy, CheckCircle, Circle, Download, MagnifyingGlass, X, Lightbulb,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { Hourglass: HGIcon, Scales, Sparkle, Cloud: CloudIcon };

export default function Apprentissage() {
  const [data, setData] = useState(null);
  const [progression, setProgression] = useState({ completed_steps: [], tour_seen: false });
  const [activeSection, setActiveSection] = useState("onboarding");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activePromptCat, setActivePromptCat] = useState("all");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/apprentissage/contenu").then((r) => setData(r.data)).catch(() => toast.error("Erreur chargement contenu"));
    api.get("/apprentissage/progression").then((r) => setProgression(r.data)).catch(() => {});
  }, []);

  const toggleStep = async (id) => {
    const set = new Set(progression.completed_steps);
    if (set.has(id)) set.delete(id); else set.add(id);
    const next = { ...progression, completed_steps: Array.from(set) };
    setProgression(next);
    try {
      await api.post("/apprentissage/progression", next);
    } catch { toast.error("Erreur sauvegarde"); }
  };

  const downloadManuel = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/apprentissage/manuel.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "vita-lex-manuel-pratique.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Manuel téléchargé");
    } catch { toast.error("Erreur téléchargement"); }
    finally { setDownloading(false); }
  };

  const filteredGlossary = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.glossary.filter((g) =>
      (activeCategory === "all" || g.categorie === activeCategory) &&
      (q === "" || g.terme.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q))
    );
  }, [data, search, activeCategory]);

  const filteredPrompts = useMemo(() => {
    if (!data) return [];
    return data.prompts.filter((p) => activePromptCat === "all" || p.categorie === activePromptCat);
  }, [data, activePromptCat]);

  if (!data) return <div className="p-12 label-mono">Chargement du centre...</div>;

  const completedCount = progression.completed_steps.length;
  const progressPct = Math.round((completedCount / data.stats.n_onboarding) * 100);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">§ 09 · Centre d'apprentissage</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
            <GraduationCap size={42} weight="duotone" className="text-[var(--signal)]" /> Apprentissage citoyen
          </h1>
          <p className="mt-3 text-[var(--muted)] max-w-2xl">
            De l'idée à l'acte juridique en moins de quinze minutes. Cinq étapes pour devenir un lead-developer assisté par IA.
          </p>
        </div>
        <button
          onClick={downloadManuel}
          disabled={downloading}
          data-testid="appr-download-manuel"
          className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-5 py-3 label-mono flex items-center gap-2 disabled:opacity-50"
        >
          <Download size={14} weight="bold" /> {downloading ? "Préparation..." : "Télécharger le manuel"}
        </button>
      </div>

      {/* Progression */}
      <div className="border border-black/15 bg-[var(--paper)] p-5 mb-8 fade-up" data-testid="appr-progression">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="label-mono">§ Progression onboarding</div>
            <div className="font-serif text-xl mt-1">{completedCount}/{data.stats.n_onboarding} étapes terminées</div>
          </div>
          <div className="font-serif text-3xl tabular-nums text-[var(--signal)]">{progressPct}%</div>
        </div>
        <div className="mt-3 h-1.5 bg-black/10">
          <div className="h-full bg-[var(--signal)] transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-px bg-black/15 border border-black/15 mb-8 overflow-x-auto" data-testid="appr-tabs">
        {[
          { id: "onboarding", label: "Onboarding", count: data.stats.n_onboarding },
          { id: "prompts", label: "Bibliothèque de prompts", count: data.stats.n_prompts },
          { id: "blueprints", label: "Cas d'usage", count: data.stats.n_blueprints },
          { id: "glossary", label: "Glossaire", count: data.stats.n_glossary },
          { id: "quizzes", label: "Quiz", count: data.stats.n_quizzes },
          { id: "annexe", label: "Annexe emergent.sh", count: null },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveSection(t.id)}
            data-testid={`appr-tab-${t.id}`}
            className={`px-5 py-3 label-mono whitespace-nowrap ${activeSection === t.id ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] hover:bg-[var(--paper-2)]"}`}
          >
            {t.label} {t.count != null && <span className="opacity-60">· {t.count}</span>}
          </button>
        ))}
      </div>

      {/* Sections */}
      {activeSection === "onboarding" && (
        <OnboardingSection steps={data.onboarding} completed={progression.completed_steps} onToggle={toggleStep} />
      )}
      {activeSection === "prompts" && (
        <PromptsSection prompts={filteredPrompts} active={activePromptCat} onCat={setActivePromptCat} all={data.prompts} />
      )}
      {activeSection === "blueprints" && (
        <BlueprintsSection blueprints={data.blueprints} />
      )}
      {activeSection === "glossary" && (
        <GlossarySection
          terms={filteredGlossary}
          categories={data.glossary_categories}
          search={search}
          onSearch={setSearch}
          activeCategory={activeCategory}
          onCategory={setActiveCategory}
        />
      )}
      {activeSection === "annexe" && (
        <AnnexeSection data={data.annexe_emergent} />
      )}
      {activeSection === "quizzes" && (
        <QuizzesSection quizzes={data.quizzes} />
      )}
    </div>
  );
}

function QuizzesSection({ quizzes }) {
  const [scores, setScores] = useState({});
  useEffect(() => {
    api.get("/apprentissage/progression").then((r) => setScores((r.data?.quizzes) || {})).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15" data-testid="appr-quizzes">
      {quizzes.map((q) => {
        const Icon = ICONS[q.icone] || Sparkle;
        const score = scores[q.id];
        return (
          <Link
            key={q.id}
            to={`/apprentissage/quiz/${q.id}`}
            data-testid={`appr-quiz-${q.id}`}
            className="bg-[var(--paper)] p-6 hover:bg-[var(--paper-2)] transition-colors group flex flex-col"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="p-3 border-2 inline-flex" style={{ borderColor: q.couleur }}>
                <Icon size={24} weight="duotone" style={{ color: q.couleur }} />
              </div>
              {score && (
                <div className="text-right">
                  <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--muted)]">Meilleur score</div>
                  <div className="font-serif text-2xl tabular-nums" style={{ color: q.couleur }}>{score.score_pct}%</div>
                  <div className="font-mono text-[10px]" style={{ color: q.couleur }}>{score.badge}</div>
                </div>
              )}
            </div>
            <h3 className="font-serif text-2xl mt-4 leading-tight">{q.titre}</h3>
            <p className="mt-2 text-sm text-[var(--muted)] flex-1">{q.description}</p>
            <div className="mt-4 flex items-center justify-between border-t border-black/15 pt-3">
              <span className="font-mono text-xs text-[var(--muted)]">{q.n_questions} questions</span>
              <span className="label-mono group-hover:text-[var(--signal)] flex items-center gap-1">{score ? "Refaire" : "Commencer"} <ArrowRight size={12} /></span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function OnboardingSection({ steps, completed, onToggle }) {
  const set = new Set(completed);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15" data-testid="appr-onboarding">
      {steps.map((s) => {
        const done = set.has(s.id);
        return (
          <div key={s.id} className="bg-[var(--paper)] p-6 flex flex-col" data-testid={`appr-step-${s.id}`}>
            <div className="flex items-start gap-4">
              <button
                onClick={() => onToggle(s.id)}
                className="shrink-0 mt-1"
                aria-pressed={done}
                data-testid={`appr-step-${s.id}-toggle`}
              >
                {done ? <CheckCircle size={24} weight="fill" className="text-[var(--signal)]" /> : <Circle size={24} className="text-black/30 hover:text-[var(--signal)] transition-colors" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="label-mono">étape {s.ordre} · {s.duree_estimee_min} min</div>
                <h3 className="font-serif text-2xl mt-1">{s.titre}</h3>
                <p className="mt-2 text-[var(--ink)] leading-relaxed">{s.description}</p>
                {s.astuce && (
                  <div className="mt-3 border-l-2 border-[var(--signal)] pl-3 py-1 bg-[var(--signal-light)]">
                    <div className="flex items-start gap-2">
                      <Lightbulb size={14} weight="fill" className="text-[var(--signal)] mt-1 shrink-0" />
                      <p className="text-sm font-serif italic">{s.astuce}</p>
                    </div>
                  </div>
                )}
                {s.concepts_cles && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {s.concepts_cles.map((c) => (
                      <span key={c} className="font-mono text-[10px] px-2 py-1 border border-black/15 text-[var(--muted)]">{c}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.actions.map((a) => (
                    <Link key={a.route} to={a.route} className="border border-black px-3 py-1.5 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2">
                      {a.label} <ArrowRight size={12} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PromptsSection({ prompts, active, onCat, all }) {
  const cats = ["all", ...Array.from(new Set(all.map((p) => p.categorie)))];
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Prompt copié");
  };
  return (
    <div data-testid="appr-prompts">
      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => onCat(c)}
            data-testid={`appr-prompt-cat-${c}`}
            className={`px-3 py-1.5 label-mono border ${active === c ? "bg-[var(--ink)] text-[var(--paper)] border-black" : "border-black/30 hover:border-black"}`}
          >
            {c === "all" ? "Tous" : c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15">
        {prompts.map((p) => (
          <div key={p.id} className="bg-[var(--paper)] p-5 flex flex-col" data-testid={`appr-prompt-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-mono">{p.categorie}</div>
                <h3 className="font-serif text-lg mt-1">{p.titre}</h3>
                <p className="mt-1 text-xs text-[var(--muted)] italic">{p.contexte}</p>
              </div>
              <button
                onClick={() => copy(p.prompt)}
                data-testid={`appr-copy-${p.id}`}
                className="shrink-0 border border-black p-2 hover:bg-[var(--signal)] hover:text-[var(--paper)] hover:border-[var(--signal)]"
                title="Copier le prompt"
              >
                <Copy size={14} />
              </button>
            </div>
            <pre className="mt-3 font-mono text-xs leading-relaxed bg-[var(--paper-2)] border border-black/15 p-3 whitespace-pre-wrap text-[var(--ink)] flex-1">{p.prompt}</pre>
            {p.comparaison && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="border-l-2 border-black/40 pl-2">
                  <div className="label-mono text-[9px]">Avant</div>
                  <p className="font-serif italic text-[var(--muted)]">{p.comparaison.avant}</p>
                </div>
                <div className="border-l-2 border-[var(--signal)] pl-2">
                  <div className="label-mono text-[9px] text-[var(--signal)]">Après</div>
                  <p className="font-serif italic">{p.comparaison.apres}</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlueprintsSection({ blueprints }) {
  return (
    <div className="space-y-px bg-black/15 border border-black/15" data-testid="appr-blueprints">
      {blueprints.map((b) => (
        <div key={b.id} className="bg-[var(--paper)] p-6" data-testid={`appr-blueprint-${b.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="label-mono">cas d'usage · {b.duree_min} min</div>
              <h3 className="font-serif text-2xl mt-1">{b.titre}</h3>
              <p className="mt-1 italic text-[var(--muted)]">{b.objectif}</p>
            </div>
          </div>
          <ol className="mt-4 space-y-2">
            {b.etapes.map((e, i) => (
              <li key={i} className="flex items-start gap-3 border-b border-black/10 pb-2">
                <span className="font-mono text-xs text-[var(--signal)] mt-1 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-mono text-[10px] px-2 py-0.5 border border-black/30 mt-0.5 shrink-0">{e.module}</span>
                <span className="flex-1">{e.label}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4 border-t border-[var(--signal)] pt-3 flex items-start gap-2">
            <PaperPlaneTilt size={16} weight="fill" className="text-[var(--signal)] mt-1 shrink-0" />
            <p><strong>Livrable :</strong> <span className="font-serif italic">{b.livrable}</span></p>
          </div>
        </div>
      ))}
    </div>
  );
}

function GlossarySection({ terms, categories, search, onSearch, activeCategory, onCategory }) {
  return (
    <div data-testid="appr-glossary">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex items-center gap-2 border border-black px-3 py-2 flex-1 min-w-[260px] bg-[var(--paper)]">
          <MagnifyingGlass size={14} className="text-[var(--muted)]" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher un terme..."
            className="bg-transparent border-0 outline-0 flex-1 font-mono text-sm"
            data-testid="appr-glossary-search"
          />
          {search && <button onClick={() => onSearch("")} className="text-[var(--muted)] hover:text-[var(--signal)]"><X size={12} /></button>}
        </div>
        <button
          onClick={() => onCategory("all")}
          className={`px-3 py-2 label-mono border ${activeCategory === "all" ? "bg-[var(--ink)] text-[var(--paper)] border-black" : "border-black/30 hover:border-black"}`}
        >
          Tous
        </button>
        {Object.entries(categories).map(([id, meta]) => {
          const Icon = ICONS[meta.icone] || Sparkle;
          return (
            <button
              key={id}
              onClick={() => onCategory(id)}
              data-testid={`appr-glossary-cat-${id}`}
              className={`px-3 py-2 label-mono border flex items-center gap-2 ${activeCategory === id ? "text-[var(--paper)] border-transparent" : "border-black/30 hover:border-black"}`}
              style={activeCategory === id ? { background: meta.couleur } : {}}
            >
              <Icon size={12} weight={activeCategory === id ? "fill" : "regular"} /> {meta.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/15 border border-black/15">
        {terms.length === 0 && (
          <div className="bg-[var(--paper)] p-8 col-span-full text-center label-mono text-[var(--muted)]">
            Aucun terme trouvé.
          </div>
        )}
        {terms.map((t) => {
          const cat = categories[t.categorie];
          return (
            <div key={t.terme} className="bg-[var(--paper)] p-5" data-testid={`appr-term-${t.terme}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-serif text-xl text-[var(--signal)]">{t.terme}</div>
                {cat && (
                  <span className="font-mono text-[9px] px-2 py-0.5 border" style={{ borderColor: cat.couleur, color: cat.couleur }}>{cat.label.split(" ")[0]}</span>
                )}
              </div>
              <p className="mt-2 text-sm leading-relaxed">{t.definition}</p>
              {t.exemple && (
                <p className="mt-2 text-xs italic text-[var(--muted)] border-l-2 border-black/20 pl-2">Ex. {t.exemple}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnnexeSection({ data }) {
  return (
    <div className="border border-black/15 bg-[var(--paper)] p-6" data-testid="appr-annexe">
      <div className="label-mono">§ Méta · emergent.sh</div>
      <h2 className="font-serif text-3xl mt-1">{data.titre}</h2>
      <p className="mt-3 max-w-3xl leading-relaxed">{data.intro}</p>

      <h3 className="mt-8 font-serif text-xl">Phases du projet</h3>
      <table className="w-full mt-3 text-sm">
        <thead className="label-mono text-left border-b border-black">
          <tr><th className="p-3">Phase</th><th className="p-3">Durée</th><th className="p-3">Action</th></tr>
        </thead>
        <tbody className="divide-y divide-black/10">
          {data.phases.map((p, i) => (
            <tr key={i}>
              <td className="p-3 font-serif">{p.phase}</td>
              <td className="p-3 font-mono text-xs text-[var(--muted)]">{p.duree}</td>
              <td className="p-3">{p.action}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="mt-8 font-serif text-xl">Agents IA mobilisés</h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/15 border border-black/15">
        {data.agents_utilises.map((a) => (
          <div key={a.role} className="bg-[var(--paper)] p-4">
            <div className="label-mono text-[var(--signal)]">{a.role}</div>
            <p className="mt-2 text-sm font-serif">{a.tache}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
